import type { ButtonHTMLAttributes, CSSProperties, ReactNode } from "react";

export interface NavigationItem {
  label: string;
  href: string;
  marker?: string;
}

interface AppShellProps {
  appName: string;
  workspaceName: string;
  navigation: NavigationItem[];
  activeHref: string;
  user: {
    name: string;
    role: string;
  };
  accent: string;
  children: ReactNode;
}

export function AppShell({
  appName,
  workspaceName,
  navigation,
  activeHref,
  user,
  accent,
  children
}: AppShellProps) {
  const initials = user.name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="app-shell" style={{ "--accent": accent } as CSSProperties}>
      <aside className="sidebar">
        <a className="brand" href="/" aria-label={`${appName} home`}>
          <span className="brand-mark">L</span>
          <span>
            <strong>Luma LMS</strong>
            <small>{appName}</small>
          </span>
        </a>

        <div className="workspace-chip">
          <span className="workspace-icon">{workspaceName.slice(0, 1)}</span>
          <span>
            <small>Workspace</small>
            <strong>{workspaceName}</strong>
          </span>
        </div>

        <nav className="navigation" aria-label="Primary navigation">
          {navigation.map((item) => (
            <a
              className={activeHref === item.href ? "active" : undefined}
              href={item.href}
              key={item.href}
            >
              <span className="nav-dot" aria-hidden="true" />
              <span>{item.label}</span>
              {item.marker ? <span className="nav-marker">{item.marker}</span> : null}
            </a>
          ))}
        </nav>

        <div className="sidebar-profile">
          <span className="avatar">{initials}</span>
          <span>
            <strong>{user.name}</strong>
            <small>{user.role}</small>
          </span>
          <button aria-label="Open account menu" type="button">•••</button>
        </div>
      </aside>

      <main className="main-content">{children}</main>
    </div>
  );
}

interface PageHeaderProps {
  eyebrow?: string;
  title: string;
  description: string;
  actions?: ReactNode;
}

export function PageHeader({ eyebrow, title, description, actions }: PageHeaderProps) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {actions ? <div className="header-actions">{actions}</div> : null}
    </header>
  );
}

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: "primary" | "secondary" | "ghost";
}

export function Button({
  children,
  className,
  variant = "primary",
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      className={[`button button-${variant}`, className].filter(Boolean).join(" ")}
      type={type}
      {...props}
    >
      {children}
    </button>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  detail: string;
  trend?: string;
}

export function StatCard({ label, value, detail, trend }: StatCardProps) {
  return (
    <article className="stat-card">
      <div className="stat-card-top">
        <span>{label}</span>
        {trend ? <span className="trend">{trend}</span> : null}
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

export function StatusPill({
  children,
  tone = "neutral"
}: {
  children: ReactNode;
  tone?: "success" | "warning" | "info" | "danger" | "neutral";
}) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function ProgressBar({ value }: { value: number }) {
  return (
    <div className="progress-track" aria-label={`${value}% complete`}>
      <span style={{ width: `${Math.min(100, Math.max(0, value))}%` }} />
    </div>
  );
}

export function PlaceholderPage({ title, description }: { title: string; description: string }) {
  return (
    <section className="placeholder-page">
      <span className="placeholder-icon">◇</span>
      <h2>{title}</h2>
      <p>{description}</p>
      <StatusPill tone="info">Mock scaffold</StatusPill>
    </section>
  );
}
